import {AfterViewInit, Component, Input} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {EmTranslatePipe, ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  DocumentName,
  INSURANCE_TYPE_OPTIONS,
  INSURER_OPTIONS,
  SALE_REPORT_STATUS_OPTIONS,
  SALE_REPORT_TYPE_OPTIONS,
} from '@emma-labs/shared/typescript';
import {saveAs} from 'file-saver';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonModule} from 'primeng/button';
import {DividerModule} from 'primeng/divider';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {TooltipModule} from 'primeng/tooltip';
import {finalize} from 'rxjs';
import {first} from 'rxjs/operators';
import {SaleReportForm} from '../../entities/page-shared-object/sale-report-form';
import {SaleReportService} from '../../services/sale/sale-report.service';

@Component({
  selector: 'em-sale-report',
  templateUrl: './sale-report.component.html',
  imports: [
    ReactiveFormsModule,
    DropdownModule,
    NgxMaskDirective,
    DividerModule,
    InputTextModule,
    ButtonModule,
    EmTranslatePipe,
    TooltipModule,
  ],
})
export class SaleReportComponent implements AfterViewInit {
  @Input() form!: FormGroup<SaleReportForm>;
  @Input() hasTitle = false;
  @Input() index = 0;
  @Input() secretCode: string | undefined | null = undefined;

  isClientAccessLinkButtonLoading = false;
  readonly insurerOptions = INSURER_OPTIONS;
  readonly statusOptions = SALE_REPORT_STATUS_OPTIONS;
  readonly typeOptions = SALE_REPORT_TYPE_OPTIONS;
  readonly insuranceTypeOptions = INSURANCE_TYPE_OPTIONS;
  readonly isFr: boolean;

  constructor(
    readonly translate: TranslationService,
    readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly saleReportService: SaleReportService,
    private readonly toastService: ToastService,
  ) {
    this.isFr = translate.currentLanguageIsFr();
  }

  ngAfterViewInit(): void {
    if (!this.form || !this.form.value.id) {
      this.toastService.error(
        this.isFr ? 'Erreur lors de la récupération du rapport de vente' : 'Error retrieving sale report',
      );
    }
  }

  openProposal(): void {
    if (!this.form.value.canAccessProposal) {
      this.toastService.error(
        this.translate.currentLanguageIsFr()
          ? "Cette fonctionnalité n'est actuellement pas disponible pour ce rapport de vente. Veuillez consulter le contrat."
          : 'This feature is currently unavailable for this sale report. Please refer to the contract.',
      );
      return;
    }
    this.router.navigate([this.form.value.proposalLink]);
  }

  getFna(): void {
    this.saleReportService
      .getDocument(this.form.value.id!, DocumentName.ARCHIVED_FNA)
      .pipe(first())
      .subscribe({
        next: (blob) =>
          saveAs(
            blob,
            this.translate.currentLanguageIsFr()
              ? 'analyse_de_besoins_financiers.pdf'
              : 'financial_needs_analysis.pdf',
          ),
        error: () =>
          this.toastService.error(
            this.translate.currentLanguageIsFr()
              ? "Cette fonctionnalité n'est actuellement pas disponible pour ce rapport de vente."
              : 'This feature is currently unavailable for this sale report.',
          ),
      });
  }

  getReplacementNotice(): void {
    this.saleReportService
      .getDocument(this.form.value.id!, DocumentName.REPLACEMENT_NOTICE)
      .pipe(first())
      .subscribe({
        next: (blob) =>
          saveAs(
            blob,
            this.translate.currentLanguageIsFr() ? 'avis_de_remplacement.pdf' : 'replacement_notice.pdf',
          ),
        error: () =>
          this.toastService.error(
            this.isFr ? 'Erreur lors du téléchargement du document' : 'Error downloading document',
          ),
      });
  }

  getContract(): void {
    this.saleReportService
      .getDocument(this.form.value.id!, DocumentName.CONTRACT)
      .pipe(first())
      .subscribe({
        next: (blob) => saveAs(blob, this.form.value.policyNumber!),
        error: () =>
          this.toastService.error(
            this.isFr ? 'Erreur lors du téléchargement du contrat' : 'Error downloading contract',
          ),
      });
  }

  sendClientSessionAccessLink(): void {
    this.isClientAccessLinkButtonLoading = true;

    this.saleReportService
      .sendClientSessionAccessLink(this.form.value.id!)
      .pipe(
        first(),
        finalize(() => (this.isClientAccessLinkButtonLoading = false)),
      )
      .subscribe({
        next: () =>
          this.toastService.success(
            this.isFr ? 'Le lien a été envoyé avec succès.' : 'The link has been sent successfully.',
          ),
        error: () =>
          this.toastService.error(
            this.isFr
              ? 'Une erreur est survenue. Veuillez réessayer.'
              : 'An error occurred. Please try again.',
          ),
      });
  }
}
